import "./styles.css";
import { apiRequest } from "./api";
import { loaderMarkup } from "./components";
import { appContent } from "./content";
import { adminCopy } from "./copy/admin";
import { loginFailureMood, loginFailureMoods, messageIsLockedOut, setJimMood, startJimIdleLoop } from "./jim";
import { appShellMarkup, workspaceShellMarkup } from "./layout";
import { renderAdminScreen } from "./screens/admin";
import { renderLoginScreen } from "./screens/login";
import { renderSearchScreen } from "./screens/search";
import { renderLibraryScreen, teardownLibraryScreen } from "./screens/library";
import type { LibrarySelection } from "./screens/library";
import { renderSavedAnswersScreen } from "./screens/workspaceViews";
import type { AdminUser, AssetDetail, ClauseDetail, ContentReportIssueType, ContentReportsPage, CurrentUser, JimMood, JimReadAdmin, NavGroup, NavRecord, ReadSuggestion, RecentQuery, ResultFeedbackAction, ResultFeedbackVerdict, SearchResult, SessionState, TuningRule, WorkspaceView, ZeroResultQuery } from "./types";
import { escapeHtml, randomBetween } from "./utils";

const state: SessionState = {
  user: null,
  csrfToken: null,
  turnstileSiteKey: null,
  loading: true,
  message: "",
  answerMode: "plain",
  savedAnswers: [],
  recentQueries: [],
  loginFailureCount: 0,
  setupCredential: null
};

const appRoot = document.querySelector<HTMLDivElement>("#app");

if (!appRoot) {
  throw new Error("App root missing");
}

const app = appRoot;

let loginSettleTimer: number | undefined;
let loginFailureVersion = 0;

function clearLoginSettleTimer(): void {
  if (loginSettleTimer !== undefined) {
    window.clearTimeout(loginSettleTimer);
    loginSettleTimer = undefined;
  }
}

function scheduleLoginFailureSettle(version: number): void {
  clearLoginSettleTimer();
  loginSettleTimer = window.setTimeout(() => {
    if (version !== loginFailureVersion || state.user || !state.message) return;
    setJimMood("idle");
    startJimIdleLoop(randomBetween(5500, 12000));
  }, 9000);
}

function messageMood(message: string): JimMood {
  if (/locked|attempts left|disabled|access/i.test(message)) return "locked";
  return "warning";
}

function loginMood(): JimMood {
  if (state.message) return state.loginFailureCount ? loginFailureMood(state.loginFailureCount) : messageMood(state.message);
  if (state.setupCredential) return "success";
  return "idle";
}

function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  return apiRequest<T>(path, options, state.csrfToken);
}

async function downloadStats(): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch("/api/admin/stats/export");
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(payload?.error || adminCopy.downloadStatsFailure);
  }
  const disposition = response.headers.get("content-disposition") || "";
  const filename = disposition.match(/filename="([^"]+)"/i)?.[1] || "ask-jim-stats.xlsx";
  return { blob: await response.blob(), filename };
}

async function listRecentQueries(): Promise<void> {
  try {
    const payload = await api<{ queries: RecentQuery[] }>("/api/search/recent");
    state.recentQueries = payload.queries;
  } catch {
    state.recentQueries = [];
  }
}

async function refreshSession(): Promise<void> {
  state.loading = true;
  render();
  try {
    const payload = await api<{ user: CurrentUser | null; csrfToken: string | null; turnstileSiteKey?: string | null }>("/api/session");
    state.user = payload.user;
    state.csrfToken = payload.csrfToken;
    state.turnstileSiteKey = payload.turnstileSiteKey || null;
    if (payload.user) {
      state.loginFailureCount = 0;
      state.message = "";
      if (!payload.user.mustChangePassword) await listRecentQueries();
    }
  } catch {
    state.user = null;
    state.csrfToken = null;
    state.turnstileSiteKey = null;
    state.recentQueries = [];
  } finally {
    state.loading = false;
    render();
  }
}

function renderShell(content: string, shellVariant: "default" | "login" = "default", settingsOpen = false): void {
  teardownLibraryScreen();
  app.innerHTML = appShellMarkup(state.user, content, shellVariant, settingsOpen);

  document.querySelector<HTMLButtonElement>("#logoutButton")?.addEventListener("click", async () => {
    await api("/api/auth/logout", { method: "POST" }).catch(() => undefined);
    state.user = null;
    state.csrfToken = null;
    state.recentQueries = [];
    state.loginFailureCount = 0;
    state.message = "";
    render();
  });

  document.querySelector<HTMLButtonElement>("#settingsButton")?.addEventListener("click", renderAdminDashboard);
}

function workspaceShell(activeView: WorkspaceView, content: string): string {
  return workspaceShellMarkup(
    activeView,
    content,
    state.user,
    state.savedAnswers.length,
    localStorage.getItem("askjim.sidebarCollapsed") === "true",
    state.recentQueries
  );
}

function bindWorkspaceNavigation(): void {
  document.querySelector<HTMLButtonElement>("#sidebarSearch")?.addEventListener("click", () => renderSearch());
  document.querySelector<HTMLButtonElement>("#sidebarSearchLegacy")?.addEventListener("click", () => renderSearch());
  document.querySelector<HTMLButtonElement>("#sidebarLibrary")?.addEventListener("click", () => renderLibrary());
  document.querySelector<HTMLButtonElement>("#sidebarSaved")?.addEventListener("click", renderSavedAnswers);
  document.querySelector<HTMLButtonElement>("#sidebarCollapse")?.addEventListener("click", () => {
    const sidebar = document.querySelector<HTMLElement>(".workspace-sidebar");
    if (!sidebar) return;
    const collapsed = !sidebar.classList.contains("collapsed");
    sidebar.classList.toggle("collapsed", collapsed);
    localStorage.setItem("askjim.sidebarCollapsed", String(collapsed));
    const button = document.querySelector<HTMLButtonElement>("#sidebarCollapse");
    if (button) {
      button.textContent = collapsed ? "»" : "«";
      button.setAttribute("aria-label", collapsed ? "Expand sidebar" : "Collapse sidebar");
      button.setAttribute("aria-pressed", String(collapsed));
    }
  });
  document.querySelectorAll<HTMLButtonElement>(".sidebar-recent-queries [data-recent-query]").forEach((button) => {
    button.addEventListener("click", () => renderSearch(button.dataset.recentQuery || ""));
  });
  document.querySelector<HTMLButtonElement>("#mobileNavAsk")?.addEventListener("click", () => renderSearch());
  document.querySelector<HTMLButtonElement>("#mobileNavLibrary")?.addEventListener("click", () => renderLibrary());
  document.querySelector<HTMLButtonElement>("#mobileNavSaved")?.addEventListener("click", renderSavedAnswers);
}

function bindCopyButtons(): void {
  document.querySelectorAll<HTMLButtonElement>(".copyButton").forEach((button) => {
    button.addEventListener("click", async () => {
      const value = button.dataset.copy || "";
      await navigator.clipboard.writeText(value);
      button.textContent = appContent.credentials.copied;
      window.setTimeout(() => {
        button.textContent = appContent.credentials.copy;
      }, 1400);
    });
  });
}

function render(): void {
  if (state.loading) {
    renderShell(`<section class="panel narrow">${loaderMarkup("thinking", appContent.loading.checkingWorkspace)}</section>`);
    return;
  }

  if (!state.user) {
    renderLogin();
    return;
  }

  if (state.user.mustChangePassword) {
    renderChangePassword(true);
    return;
  }

  // A shared #/clause/… or #/asset/… link opens that record rather than
  // dropping the reader on the search screen.
  const deepLink = parseLibraryHash(window.location.hash);
  if (deepLink) renderLibrary(deepLink);
  else renderSearch();
}

/** Leaving the library drops its deep link, so a reload lands where you are. */
function clearLibraryHash(): void {
  if (!parseLibraryHash(window.location.hash)) return;
  suppressHashRoute = true;
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
  suppressHashRoute = false;
}

function renderLogin(): void {
  renderLoginScreen({
    message: state.message,
    setupCredential: state.setupCredential,
    turnstileSiteKey: state.turnstileSiteKey,
    loginMood,
    renderShell: (content, shellVariant) => renderShell(content, shellVariant),
    beforeLoginAttempt: clearLoginSettleTimer,
    bindCopyButtons,
    login: (username, password, turnstileToken) => api<{ user: CurrentUser; csrfToken: string }>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({
          username,
          password,
          ...(turnstileToken ? { turnstileToken } : {})
        })
      }),
    onLoginSuccess: async (payload) => {
      state.loginFailureCount = 0;
      state.message = "";
      state.user = payload.user;
      state.csrfToken = payload.csrfToken;
      if (!payload.user.mustChangePassword) await listRecentQueries();
      render();
    },
    onLoginFailure: (message) => {
      state.message = message;
      state.loginFailureCount = messageIsLockedOut(state.message)
        ? loginFailureMoods.length
        : Math.min(state.loginFailureCount + 1, loginFailureMoods.length);
      loginFailureVersion += 1;
      renderLogin();
      scheduleLoginFailureSettle(loginFailureVersion);
    }
  });
}

function renderChangePassword(firstLogin: boolean): void {
  const mustSetEmail = state.user?.mustSetEmail === true;
  renderShell(`
    <section class="panel narrow">
      <h1>${firstLogin ? appContent.password.firstLoginHeading : appContent.password.heading}</h1>
      <form id="passwordForm" class="stack">
        <label>${appContent.password.currentPassword}
          <input name="currentPassword" type="password" autocomplete="current-password" required />
        </label>
        <label>${appContent.password.newPassword}
          <input name="newPassword" type="password" autocomplete="new-password" minlength="14" required />
        </label>
        ${mustSetEmail ? `
          <label>${appContent.password.recoveryEmail}
            <input name="recoveryEmail" type="email" autocomplete="email" required />
          </label>
          <label>${appContent.password.confirmRecoveryEmail}
            <input name="confirmRecoveryEmail" type="email" autocomplete="email" required />
          </label>
        ` : ""}
        <button type="submit">${appContent.password.submit}</button>
      </form>
      <p class="status">${escapeHtml(state.message)}</p>
    </section>
  `);

  document.querySelector<HTMLFormElement>("#passwordForm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget as HTMLFormElement);
    const recoveryEmail = String(form.get("recoveryEmail") || "");
    if (mustSetEmail && recoveryEmail !== String(form.get("confirmRecoveryEmail") || "")) {
      state.message = appContent.password.recoveryEmailMismatch;
      renderChangePassword(firstLogin);
      return;
    }
    try {
      await api("/api/auth/change-password", {
        method: "POST",
        body: JSON.stringify({
          currentPassword: String(form.get("currentPassword") || ""),
          newPassword: String(form.get("newPassword") || ""),
          ...(mustSetEmail ? { recoveryEmail } : {})
        })
      });
      state.message = "";
      await refreshSession();
    } catch (error) {
      state.message = error instanceof Error ? error.message : appContent.password.failure;
      renderChangePassword(firstLogin);
    }
  });
}

function renderAdminDashboard(): void {
  clearLibraryHash();
  renderAdminScreen({
    currentUser: state.user,
    message: state.message,
    setupCredential: state.setupCredential,
    renderWorkspace: (content) => renderShell(workspaceShell("settings", content), "default", true),
    bindWorkspaceNavigation,
    bindCopyButtons,
    reloadAdmin: renderAdminDashboard,
    listUsers: () => api<{ users: Array<AdminUser> }>("/api/admin/users"),
    createUser: (username, role) => api("/api/admin/users", {
        method: "POST",
        body: JSON.stringify({
          username,
          role
        })
      }),
    resetPassword: (userId) => api(`/api/admin/users/${userId}/reset-password`, {
      method: "POST"
    }),
    setLicensedAccess: (userId, enabled) => api(`/api/admin/users/${userId}/licensed-access`, {
      method: "POST",
      body: JSON.stringify({ enabled })
    }),
    disableUser: (userId) => api(`/api/admin/users/${userId}/disable`, { method: "POST" }),
    enableUser: (userId) => api(`/api/admin/users/${userId}/enable`, { method: "POST" }),
    deleteUser: (userId) => api(`/api/admin/users/${userId}/delete`, { method: "POST" }),
    listTuning: () => api<{ rules: TuningRule[]; zeroResultQueries: ZeroResultQuery[] }>("/api/admin/tuning"),
    createTuning: (body) => api<TuningRule>("/api/admin/tuning", { method: "POST", body: JSON.stringify(body) }),
    deleteTuning: (id) => api(`/api/admin/tuning/${id}/delete`, { method: "POST" }),
    listReads: () => api<{ reads: JimReadAdmin[]; suggestions: ReadSuggestion[] }>("/api/admin/reads"),
    createRead: (clauseNumber, body) => api<JimReadAdmin>("/api/admin/reads", { method: "POST", body: JSON.stringify({ clauseNumber, body }) }),
    updateRead: (id, body, updatedAt) => api<JimReadAdmin>(`/api/admin/reads/${id}`, { method: "POST", body: JSON.stringify({ body, updatedAt }) }),
    approveRead: (id, updatedAt) => api<JimReadAdmin>(`/api/admin/reads/${id}/approve`, { method: "POST", body: JSON.stringify({ updatedAt }) }),
    deleteRead: (id) => api(`/api/admin/reads/${id}/delete`, { method: "POST" }),
    listReports: (status, page) => api<ContentReportsPage>(`/api/admin/reports?page=${page}${status ? `&status=${encodeURIComponent(status)}` : ""}`),
    updateReportStatus: (id, status, adminNote) => api(`/api/admin/reports/${id}/status`, { method: "POST", body: JSON.stringify({ status, admin_note: adminNote }) }),
    downloadStats,
    changePassword: (currentPassword, newPassword) => api("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword })
    }),
    changeEmail: (email, confirmEmail) => api<{ email: string }>("/api/account/change-email", {
      method: "POST",
      body: JSON.stringify({ email, confirmEmail })
    }),
    onCredential: (credential) => {
      state.setupCredential = credential;
      state.message = "";
    },
    onMessage: (message) => {
      state.message = message;
    },
    onUserDisabled: (username) => {
      state.message = `${username} ${appContent.admin.disabledSuffix}`;
      state.setupCredential = null;
    }
  });
}

// Deep links: #/clause/<id> and #/asset/<id>. The kind is in the URL so a cold
// load knows which endpoint to call without waiting for the record list.
function parseLibraryHash(hash: string): LibrarySelection | undefined {
  const match = hash.match(/^#\/(clause|asset)\/([^/?#]+)$/);
  if (!match) return undefined;
  try {
    // A shared link can arrive malformed (`#/clause/%`). Left uncaught, the
    // URIError escapes render() and no authenticated UI loads at all.
    return { kind: match[1] as "clause" | "asset", id: decodeURIComponent(match[2]) };
  } catch {
    return undefined;
  }
}

let suppressHashRoute = false;

function renderLibrary(selected?: LibrarySelection): void {
  renderLibraryScreen({
    selected,
    renderWorkspace: (content) => renderShell(workspaceShell("library", content)),
    bindWorkspaceNavigation,
    openSearch: renderSearch,
    saveAnswer,
    openSavedAnswers: renderSavedAnswers,
    listNavigation: () => api<{ records: NavRecord[]; groups: NavGroup[] }>("/api/navigation"),
    filterNavigation: (q) => api<{ ids: string[] }>(`/api/navigation/filter?q=${encodeURIComponent(q)}`),
    getClause: (id) => api<ClauseDetail>(`/api/clauses/${id}`),
    getAsset: (id) => api<AssetDetail>(`/api/assets/${id}`),
    copyClause: (id, format) => api<{ content: string }>(`/api/clauses/${id}/copy`, { method: "POST", body: JSON.stringify({ format }) }),
    screenGrab: (recordIds) => api<{ stamp: string }>("/api/clauses/screen-grab", { method: "POST", body: JSON.stringify({ recordIds }) }),
    reportRecord: (report: { record_id: string; record_kind: "clause" | "figure" | "table"; issue_type: ContentReportIssueType; note: string }) => api<{ ok: true }>("/api/reports", { method: "POST", body: JSON.stringify(report) }),
    setLocation: (selection) => {
      const next = `#/${selection.kind}/${encodeURIComponent(selection.id)}`;
      if (window.location.hash === next) return;
      // The screen already moved, so the resulting hashchange is an echo.
      suppressHashRoute = true;
      window.history.pushState(null, "", next);
      suppressHashRoute = false;
    }
  });
}

function openClauseById(id: string): void {
  renderLibrary({ kind: "clause", id });
}

window.addEventListener("hashchange", () => {
  if (suppressHashRoute || !state.user || state.user.mustChangePassword) return;
  const selection = parseLibraryHash(window.location.hash);
  // Back out of the first library hash and the URL returns to the no-hash
  // entry, which is the search screen. Without this the library stayed on
  // screen contradicting the URL, and a reload then jumped to search.
  if (selection) renderLibrary(selection);
  else renderSearch();
});

function saveAnswer(question: string, summary: string, clauses: string[]): void {
  const id = `${Date.now()}-${clauses.join("-")}`;
  const existing = state.savedAnswers.find((item) => item.question === question && item.clauses.join("|") === clauses.join("|"));
  if (existing) return;
  state.savedAnswers.unshift({ id, question, summary, clauses });
}

function renderSavedAnswers(): void {
  clearLibraryHash();
  renderSavedAnswersScreen({
    savedAnswers: state.savedAnswers,
    renderWorkspace: (content) => renderShell(workspaceShell("saved", content)),
    bindWorkspaceNavigation,
    openSearch: () => renderSearch(),
    removeSavedAnswer: (id) => {
      state.savedAnswers = state.savedAnswers.filter((item) => item.id !== id);
    },
    rerender: renderSavedAnswers
  });
}

function renderSearch(prefillQuery = ""): void {
  clearLibraryHash();
  renderSearchScreen({
    prefillQuery,
    answerMode: state.answerMode,
    renderWorkspace: (content) => renderShell(workspaceShell("search", content)),
    bindWorkspaceNavigation,
    setAnswerMode: (mode) => {
      state.answerMode = mode;
    },
    openSearch: renderSearch,
    saveAnswer,
    openClause: openClauseById,
    sendResultFeedback: (searchEventId: string, clauseId: string, verdict: ResultFeedbackAction) => api<{ verdict: ResultFeedbackVerdict | null }>("/api/search/feedback", {
      method: "POST",
      body: JSON.stringify({ searchEventId, clauseId, verdict })
    }),
    search: async (query, searchSessionId, refinedFromSearchEventId) => {
      const payload = await api<{ searchEventId: string; searchSessionId: string; message?: string; results: SearchResult[] }>("/api/search", {
        method: "POST",
        body: JSON.stringify({ query, searchSessionId, refinedFromSearchEventId })
      });
      await listRecentQueries();
      return payload;
    }
  });
}

void refreshSession();
